import { useState, useMemo } from 'react';
import {
  LayoutTemplate,
  Search,
  Pin,
  Calendar,
  Eye,
  Megaphone,
  Sparkles,
  Layers,
  CheckCircle2,
  X,
  FileText,
} from 'lucide-react';
import type { ComunicadosConfig, ComunicadoItem } from './ComunicadosConfig';
import { ComunicadoDetailModalCore } from './ComunicadoDetailModalCore';

interface Props {
  config: {
    branding: {
      tenantName: string;
      tenantBadge?: string;
      logoUrl?: string;
      primaryColor?: string;
      secondaryColor?: string;
      slogan?: string;
      autoridadeNome?: string;
      autoridadeCargo?: string;
    };
    scope?: string;
    scopeId?: string;
  };
  comunicados: ComunicadoItem[];
  isLoading?: boolean;
}

export function ComunicadosMuralCore({ config, comunicados, isLoading }: Props) {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [onlyPinned, setOnlyPinned] = useState(false);
  const [detailItem, setDetailItem] = useState<ComunicadoItem | null>(null);

  const filtered = useMemo(() => {
    return comunicados.filter((c) => {
      if (c.ativo === false) return false;
      if (onlyPinned && !c.fixado) return false;
      if (filterCat && c.categoria !== filterCat) return false;
      if (search) {
        const q = search.toLowerCase();
        const mTitle = (c.titulo || '').toLowerCase().includes(q);
        const mCorpo = (c.corpo || '').toLowerCase().includes(q);
        if (!mTitle && !mCorpo) return false;
      }
      return true;
    });
  }, [comunicados, search, filterCat, onlyPinned]);

  const fmtDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return d;
    }
  };

  return (
    <div className="cmc-page animate-fadeIn">
      {/* Hero Banner Mural */}
      <div className="cmc-hero" style={{ background: 'linear-gradient(135deg, #0f766e 0%, #042f2e 100%)' }}>
        <div className="cmc-hero-circle c1" />
        <div className="cmc-hero-circle c2" />
        <div className="cmc-hero-content">
          <div>
            <h1 className="cmc-hero-title">
              <LayoutTemplate size={22} />
              Mural Digital Oficial
            </h1>
            <p className="cmc-hero-subtitle">
              Quadro de avisos dinâmico, comunicados fixados e campanhas ativas de {config.branding.tenantName}.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '.76rem', color: '#ccfbf1', background: 'rgba(255,255,255,0.15)', padding: '4px 10px', borderRadius: 20, fontWeight: 700 }}>
              {filtered.length} avisos no mural
            </span>
          </div>
        </div>
      </div>

      {/* Container Principal */}
      <div className="cmc-container">
        {/* Toolbar de Filtros do Mural */}
        <div className="mdc-toolbar">
          <div className="mdc-toolbar-left">
            <div className="mdc-search-box">
              <Search size={14} className="mdc-search-icon" />
              <input
                type="text"
                className="mdc-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar avisos e folders no mural digital..."
              />
              {search && (
                <button
                  type="button"
                  className="mdc-search-clear"
                  onClick={() => setSearch('')}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="mdc-cats">
            <button
              type="button"
              className={`mdc-cat-pill ${!filterCat && !onlyPinned ? 'active' : ''}`}
              onClick={() => {
                setFilterCat('');
                setOnlyPinned(false);
              }}
            >
              Todos ({comunicados.length})
            </button>
            <button
              type="button"
              className={`mdc-cat-pill ${onlyPinned ? 'active' : ''}`}
              onClick={() => setOnlyPinned(!onlyPinned)}
              style={onlyPinned ? { background: '#2563eb', color: 'white', borderColor: '#2563eb' } : undefined}
            >
              <Pin size={12} /> Fixados no Topo
            </button>
            <button
              type="button"
              className={`mdc-cat-pill ${filterCat === 'OPERACIONAL' ? 'active' : ''}`}
              onClick={() => setFilterCat(filterCat === 'OPERACIONAL' ? '' : 'OPERACIONAL')}
            >
              Operacional
            </button>
            <button
              type="button"
              className={`mdc-cat-pill ${filterCat === 'INSTITUCIONAL' ? 'active' : ''}`}
              onClick={() => setFilterCat(filterCat === 'INSTITUCIONAL' ? '' : 'INSTITUCIONAL')}
            >
              Institucional
            </button>
            <button
              type="button"
              className={`mdc-cat-pill ${filterCat === 'PEDAGOGICO' ? 'active' : ''}`}
              onClick={() => setFilterCat(filterCat === 'PEDAGOGICO' ? '' : 'PEDAGOGICO')}
            >
              Pedagógico
            </button>
          </div>
        </div>

        {/* Grid Visual de Cartões do Mural */}
        <div className="mdc-scroll-area">
          {isLoading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
              Carregando mural digital...
            </div>
          ) : filtered.length === 0 ? (
            <div className="mdc-empty">
              <div className="mdc-empty-circle" style={{ background: '#f0fdfa', color: '#0d9488' }}>
                <LayoutTemplate size={36} />
              </div>
              <h3 className="mdc-empty-title">Nenhum aviso no mural</h3>
              <p className="mdc-empty-desc">
                Não há comunicados vigentes correspondentes aos filtros selecionados.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
              padding: 4,
            }}>
              {filtered.map((c) => {
                const hasMedia = Boolean(c.configPreview?.midiaUrl);
                return (
                  <div
                    key={c.id}
                    className="mdc-card"
                    style={{
                      border: c.fixado ? '2px solid #2563eb' : '1px solid #e2e8f0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setDetailItem(c)}
                  >
                    {hasMedia && (
                      <div className="mdc-card-img-wrap" style={{ height: 160 }}>
                        <img src={c.configPreview?.midiaUrl} alt={c.titulo} loading="lazy" />
                        <div className="mdc-card-overlay">
                          <Eye size={20} />
                        </div>
                      </div>
                    )}

                    <div className="mdc-card-body" style={{ padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span
                          className="mdc-card-badge"
                          style={{
                            background: c.categoria === 'INSTITUCIONAL' ? '#f5f3ff' : c.categoria === 'PEDAGOGICO' ? '#ecfdf5' : '#eff6ff',
                            color: c.categoria === 'INSTITUCIONAL' ? '#7c3aed' : c.categoria === 'PEDAGOGICO' ? '#059669' : '#2563eb',
                          }}
                        >
                          {c.categoria}
                        </span>

                        {c.fixado && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.65rem', fontWeight: 700, color: '#2563eb' }}>
                            <Pin size={11} /> FIXADO
                          </span>
                        )}
                      </div>

                      <h4 style={{ margin: '0 0 6px', fontSize: '.88rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>
                        {c.titulo}
                      </h4>

                      <p style={{
                        margin: '0 0 12px',
                        fontSize: '.74rem',
                        color: '#64748b',
                        lineHeight: 1.45,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {c.corpo}
                      </p>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: 10, fontSize: '.68rem', color: '#94a3b8' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={12} /> {fmtDate(c.criadoEm)}
                        </span>
                        <span style={{ fontWeight: 700, color: '#2563eb' }}>
                          {c._count?.visualizacoes || 0} leituras
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalhe Completo do Comunicado */}
      {detailItem && (
        <ComunicadoDetailModalCore
          comunicado={detailItem}
          config={config as any}
          onClose={() => setDetailItem(null)}
          onEdit={() => {}}
        />
      )}
    </div>
  );
}
